import { AutocompleteV2, AutocompleteV2Props, Box } from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { useDboUrlParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { useInstanceDetails } from 'features/db-optimization/v2/hooks/useInstanceDetails';
import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';
import { DatabaseViewModelV2 } from 'features/db-optimization/v2/types';
import {
  isDboParentRoute,
  useDboPathActive,
  useVisibleDboNavItems,
} from 'pages/db-optimization/v2/DbOptimizationControllerV2/utils';

import { SidebarItem } from '../../SidebarItem';
import { SidebarSection } from '../../SidebarSection';
import { sidebarItemListSx } from '../../sidebarStyles';
import { DboParentNavItem } from './DboParentNavItem';

type DboDatabaseNavItemsProps = {
  organizationId: string;
  isLoadingInstances: boolean;
};

export const DboDatabaseNavItems = ({
  isLoadingInstances,
}: DboDatabaseNavItemsProps) => {
  const navigate = useNavigate();
  const isPathActive = useDboPathActive();
  const { dbServerId, databaseId } = useDboUrlParams();
  const { isLoading, data, error } = useInstanceDetails();

  const dbParams = dbServerId && databaseId ? { dbServerId, databaseId } : null;

  const visibleItems = useVisibleDboNavItems('database', {
    isOptimized: true,
    isManualProvider: false,
  });

  const handleDatabaseChange: AutocompleteV2Props<DatabaseViewModelV2>['onChange'] =
    ({ value: newDatabaseId }) => {
      if (!dbServerId || !newDatabaseId) {
        return;
      }
      navigate(
        buildDboRoute(DboRoute.DB_DASHBOARD, {
          dbServerId,
          databaseId: newDatabaseId,
        })
      );
    };

  const noDatabases = data?.databases?.length === 0;

  return (
    <>
      <SidebarSection>Database</SidebarSection>
      <Box mb={8}>
        <AutocompleteV2
          size="small"
          placeholder={
            isLoadingInstances || isLoading
              ? 'Loading...'
              : error
              ? 'Error loading databases'
              : noDatabases
              ? 'No databases available'
              : 'Select database'
          }
          items={data?.databases || []}
          getLabel={({ name, id }) => name ?? id ?? ''}
          getStringValue={({ id }) => id ?? ''}
          value={databaseId}
          onChange={handleDatabaseChange}
          disabled={noDatabases}
        />
      </Box>
      {dbParams ? (
        <Box sx={sidebarItemListSx}>
          {visibleItems.map((item) => {
            if (isDboParentRoute(item)) {
              return (
                <DboParentNavItem
                  key={item.title}
                  item={item}
                  params={dbParams}
                />
              );
            }

            const path = buildDboRoute(item.route, dbParams);
            return (
              <SidebarItem
                key={item.route}
                title={item.title}
                to={path}
                active={isPathActive(path)}
              />
            );
          })}
        </Box>
      ) : null}
    </>
  );
};
